/**
 * Cookie Consent & Privacy Preference Service
 * EM Asesoría Inmobiliaria y Jurídica
 * Conforme a LFPDPPP y estándares de privacidad
 */

const COOKIE_CONSENT_KEY = 'em_cookie_consent_v1';
const COOKIE_PREFERENCES_EVENT = 'em_open_cookie_preferences';
const COOKIE_CONSENT_CHANGED_EVENT = 'em_cookie_consent_changed';

export const DEFAULT_CONSENT = {
  essential: true, // Always required for app operation
  analytics: false,
  marketing: false,
  timestamp: null,
  version: '1.0'
};

/**
 * Retrieves current cookie consent preferences from localStorage
 * @returns {Object|null} Saved preferences or null if not yet answered
 */
export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      timestamp: parsed.timestamp || null,
      version: parsed.version || '1.0'
    };
  } catch (e) {
    console.warn('Error reading cookie consent:', e);
    return null;
  }
};

/**
 * Saves cookie consent preferences to localStorage and dispatches event
 * @param {Object} preferences { analytics: boolean, marketing: boolean }
 */
export const saveCookieConsent = ({ analytics = false, marketing = false }) => {
  try {
    const consent = {
      essential: true,
      analytics: Boolean(analytics),
      marketing: Boolean(marketing),
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));

    // Dispatch global event for listeners (e.g. analytics scripts)
    window.dispatchEvent(
      new CustomEvent(COOKIE_PREFERENCES_EVENT, { detail: consent })
    );

    // Apply or block analytics immediately
    applyConsentToScripts(consent);

    return consent;
  } catch (e) {
    console.error('Error saving cookie consent:', e);
    return null;
  }
};

/**
 * Triggers modal to reconfigure cookie preferences from anywhere in the app
 */
export const openCookiePreferencesModal = () => {
  window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_EVENT));
};

/**
 * Subscribes to preference open requests
 */
export const subscribeToOpenPreferences = (callback) => {
  const handler = () => callback();
  window.addEventListener(COOKIE_PREFERENCES_EVENT, handler);
  return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, handler);
};

/**
 * Subscribes to consent updates
 */
export const subscribeToConsentChanges = (callback) => {
  const handler = (e) => callback(e.detail);
  window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, handler);
};

/**
 * Applies or revokes consent in runtime
 */
export const applyConsentToScripts = (consent) => {
  if (typeof window === 'undefined') return;

  if (consent?.analytics) {
    // Analytics allowed: enable Google Analytics / telemetry if configured
    window['ga-disable-analytics'] = false;
  } else {
    // Block analytics
    window['ga-disable-analytics'] = true;
  }
};
